import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Radio, Space } from '@bedrock/components';
import FilterBar, {
  SupplierFilterEnum,
  ViewCurrency,
} from '../components/FilterBar';
import ExpertView from '../components/ExpertView';
import SupplierView from '../components/SupplierView';
import styles from './index.less';
import classNames from 'classnames/bind';
import { useDemandOptions } from '#features/useDemandOptions';
import {
  CurrencyEnum,
  demandDetailHisRound,
  DemandDetailsResDtoDemandSupplierDto,
  DemandSourceCustomInspectionVo,
  DemandSourceSupplierKolVo,
} from '#services/api';
import { useMemoizedFn } from 'ahooks';
import { ProTablePlusFormRef } from '#components/ProTablePlusForm';
import SearchFilterBar from '../components/SearchFilterBar';
import { formatData, sortBySupplier } from './format';
import { isBlank } from '@bedrock/utils';
import { FullScreenButton } from '@mpc/components';
import RejectSuppliers from '../components/RejectSuppliers';
import { useChangeRate } from '#hooks/useChangeRate';
import { KOLDemandCategory } from './type';
import { uniqBy, omit } from 'lodash';
import { useScreenAdapterContext } from '@bedrock/eed-react';
import { useFilterModel } from './useFilterConfig';
import { SignOut } from '@bedrock/icons-react';

const cx = classNames.bind(styles);

interface KOLTableData {
  demand_source_supplier_kol_vos: DemandSourceSupplierKolVo[];
  demand_suppliers: DemandDetailsResDtoDemandSupplierDto[];
}

interface KOLTableProps {
  /** 数据变化回调 */
  value?: KOLTableData;
  onChange?: (data: any) => void;
  onDataChange?: (data: any[]) => void;
  setIsFullscreen?: (isFullscreen: boolean) => void;
  showPlannerAuditColumn?: { editable?: boolean } | false;
  showMarketingAuditColumn?: { editable?: boolean } | false;
  turnsNumber?: number;
  demandId?: number;
  demandState?: string;
  showSupplierTag?: boolean;
  defaultTab?: 'expert' | 'supplier';
  // 类型：直播单、图文单
  category?: KOLDemandCategory;
  /** 定制排查列表 */
  customInspections?: DemandSourceCustomInspectionVo[];
  /** 定制排查ID列表 */
  customInspectionIdList?: number[];
}

const TAB_OPTIONS = [
  { name: '达人视图', value: 'expert' },
  { name: '供应商视图', value: 'supplier' },
];

const omitFields = ['__table_row_key__', 'parent', 'children'];

/**
 * 策划预选达人表格组件
 * 支持达人视图和供应商视图切换
 */
const KOLTable: React.FC<KOLTableProps> = forwardRef<
  ProTablePlusFormRef,
  KOLTableProps
>((props, ref) => {
  const {
    value = {
      demand_source_supplier_kol_vos: [],
      demand_suppliers: [],
    },
    onChange,
    showPlannerAuditColumn = false,
    showMarketingAuditColumn = false,
    turnsNumber = 1,
    demandId,
    demandState,
    showSupplierTag = true,
    defaultTab = 'expert',
    category = 'live',
    customInspections,
    customInspectionIdList = [],
  } = props;

  /** 达人视图表格引用 */
  const expertTableRef = useRef<any>(null);
  /** 供应商视图表格引用 */
  const supplierTableRef = useRef<any>(null);
  /** 搜索筛选栏引用 */
  const searchFilterBarRef = useRef<any>(null);
  /** 当前激活的tab */
  const [activeTab, setActiveTab] = useState<'expert' | 'supplier'>(defaultTab);
  /** 当前数据 */
  const [data, setData] = useState(() =>
    activeTab === 'expert'
      ? formatData(value.demand_source_supplier_kol_vos, customInspectionIdList)
      : sortBySupplier(
          value.demand_source_supplier_kol_vos,
          customInspectionIdList
        )
  );
  const { viewBase } = useScreenAdapterContext();
  const dataRef = useRef(data);
  /** 是否显示加载中 */
  const [loading, setLoading] = useState(false);
  /** 历史轮次数据缓存 */
  const historyRoundDataCache = useRef<{
    [key: number]: KOLTableData;
  }>({});

  /** 拒单供应商数据 */
  const [rejectSupplierData, setRejectSupplierData] = useState<
    DemandDetailsResDtoDemandSupplierDto[]
  >(() => value.demand_suppliers || []);

  const { changeRateList } = useChangeRate();

  useEffect(() => {
    setRejectSupplierData(value.demand_suppliers || []);
  }, [JSON.stringify(value.demand_suppliers)]);

  /** 轮次选项 */
  const turnsOptions = useMemo(() => {
    return Array.from({ length: turnsNumber ?? 1 }, (_, index) => ({
      name: `${index + 1}轮`,
      value: index + 1,
    })).reverse();
  }, [value]);

  // 筛选状态
  const [viewCurrency, setViewCurrency] = useState<ViewCurrency>('original');
  const [currentRound, setCurrentRound] = useState<number>(
    turnsOptions[0].value
  );
  
  const { optionsMap } = useDemandOptions({
    keys: ['main_platform'],
  });
  const customInspectionOptions = useMemo(() => {
    return [{ name: '指定达人', value: 'by_plan' }].concat(
      (customInspections || []).map((item, index) => {
        return {
          name: `定制排查${index + 1}-${item.main_platform}`,
          value: item.id,
        };
      }) as any[]
    );
  }, [customInspections]);

  /** 最终视图币种 */
  const finalViewCurrency = useMemo(() => {
    return viewCurrency === 'original'
      ? undefined
      : (viewCurrency.toUpperCase() as CurrencyEnum);
  }, [viewCurrency]);

  /** 是否是最新一轮次 */
  const isLatestRound = useMemo(() => {
    return currentRound === turnsOptions[0].value;
  }, [currentRound]);

  const finalShowPlannerAuditColumn = useMemo(() => {
    if (showPlannerAuditColumn && showPlannerAuditColumn?.editable) {
      return isLatestRound ? showPlannerAuditColumn : false;
    }
    return showPlannerAuditColumn;
  }, [isLatestRound, showPlannerAuditColumn]);

  const finalShowMarketingAuditColumn = useMemo(() => {
    if (showMarketingAuditColumn && showMarketingAuditColumn?.editable) {
      return isLatestRound ? showMarketingAuditColumn : false;
    }
    return showMarketingAuditColumn;
  }, [isLatestRound, showMarketingAuditColumn]);

  // 使用筛选模型 Hook
  const {
    filterData,
    setFilterData,
    config: filterConfig,
    resetFilter,
    handleDataFilter,
  } = useFilterModel(
    {
      activeTab,
      category,
      demandState,
      changeRateList,
      viewCurrency,
      optionsMap,
      customInspectionOptions,
      showPlannerAuditColumn: finalShowPlannerAuditColumn,
      customInspectionIdList,
    },
    searchFilterBarRef
  );

  /** 全屏状态 */
  const wrapperRef = useRef(null);

  /** 重置筛选并重置轮次 */
  const handleResetFilter = useMemoizedFn(async () => {
    setCurrentRound(turnsOptions[0].value);
    await resetFilter();
  });

  /** 供应商筛选变化回调 */
  const handleSupplierFilterChange = useMemoizedFn(
    (filter: SupplierFilterEnum) => {
      setFilterData((prev) => ({
        ...prev,
        only_lowest_price: filter === SupplierFilterEnum.LOWEST,
      }));
    }
  );

  useImperativeHandle(ref, () => {
    if (expertTableRef.current) {
      return {
        ...expertTableRef.current,
        validateFields: async () => {
          await handleResetFilter();
          const promiseList = [
            expertTableRef.current?.validateFieldsAndScroll?.(),
            supplierTableRef.current?.validateFieldsAndScroll?.(),
          ];
          if (
            typeof showMarketingAuditColumn === 'object' &&
            showMarketingAuditColumn?.editable
          ) {
            promiseList.push(
              new Promise((resolve, reject) => {
                value.demand_source_supplier_kol_vos.some(
                  (item) => !!item.principal_confirm
                )
                  ? resolve(true)
                  : reject('至少有一项需求方确认不能为空');
              })
            );
          }

          return Promise.all(promiseList);
        },
      };
    }
    return {} as ProTablePlusFormRef;
  });

  /** 筛选数据变化回调 */
  useEffect(() => {
    // 异步执行筛选，避免阻塞
    setTimeout(() => {
      if (currentRound === turnsOptions[0].value) {
        handleDataFilter(value.demand_source_supplier_kol_vos, dataRef, setData, setLoading);
      } else {
        const roundData = historyRoundDataCache.current[currentRound];
        if (roundData) {
          handleDataFilter(
            roundData.demand_source_supplier_kol_vos,
            dataRef, 
            setData, 
            setLoading
          );
        }
      }
    }, 0);
  }, [
    JSON.stringify(filterData),
    activeTab,
    JSON.stringify(value.demand_source_supplier_kol_vos),
    viewCurrency,
    currentRound // 添加 currentRound 依赖
  ]);

  /**
   * 数据变化回调
   * @param data 数据
   * @param changeData 变化数据
   * @param updateByKey 更新键
   */
  const handleDataChange = useMemoizedFn(
    (data: any[], changeData: any, updateByKey: string = 'id') => {
      const updateMap = {};
      changeData.updateData.forEach((item: any) => {
        updateMap[item[updateByKey]] = item;
      });
      if (isBlank(updateMap)) {
        return;
      }
      const newValue = value.demand_source_supplier_kol_vos.map((item: any) => {
        if (updateMap[item[updateByKey]]) {
          return { ...item, ...updateMap[item[updateByKey]] };
        }
        return item;
      });

      onChange?.({ ...value, demand_source_supplier_kol_vos: newValue });

      const newData = data.map((item: any) => {
        let res = item;
        if (updateMap[item[updateByKey]]) {
          res = {
            ...item,
            ...updateMap[item[updateByKey]],
          };
        }
        return omit(res, omitFields);
      });

      // setData(newData);
      dataRef.current = newData;
    }
  );

  // 达人视图数据变化回调
  const handleExpertDataChange = useMemoizedFn(
    (data: any[], changeData: any) => {
      const updateData = changeData.updateData.map((item: any) => {
        if (showPlannerAuditColumn && showPlannerAuditColumn?.editable) {
          /** 策划预筛选只需要更新 plan_suggestion*/
          return {
            index: item.index,
            plan_suggestion: item.plan_suggestion,
          };
        } else if (
          showMarketingAuditColumn &&
          showMarketingAuditColumn?.editable
        ) {
          /** 需求方确认只需要更新 principal_confirm*/
          return {
            index: item.index,
            principal_confirm: item.principal_confirm,
          };
        }
        return item;
      });

      handleDataChange(
        data,
        { ...changeData, updateData: updateData },
        'index'
      );
    }
  );

  const onRoundChange = useMemoizedFn(async (round: number) => {
    if (!demandId) return;
    await handleResetFilter();
    setCurrentRound(round);
    if (round === turnsOptions[0].value) {
      setRejectSupplierData(value.demand_suppliers);
      handleDataFilter(value.demand_source_supplier_kol_vos, dataRef, setData, setLoading);
    } else if (historyRoundDataCache.current[round]) {
      setRejectSupplierData(
        historyRoundDataCache.current[round].demand_suppliers
      );
      handleDataFilter(
        historyRoundDataCache.current[round].demand_source_supplier_kol_vos,
        dataRef,
        setData,
        setLoading
      );
    } else {
      try {
        setLoading(true);
        const res = await demandDetailHisRound({
          demand_id: demandId,
          turns_number: round,
        });
        setRejectSupplierData(res.data.demand_suppliers);
        historyRoundDataCache.current[round] = {
          demand_source_supplier_kol_vos:
            res.data.demand_source_detail_vo.demand_source_supplier_kol_vos,
          demand_suppliers: res.data.demand_suppliers,
        };
        handleDataFilter(
          historyRoundDataCache.current[round].demand_source_supplier_kol_vos,
          dataRef,
          setData,
          setLoading
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  });

  const handleTabChange = useMemoizedFn((tab: 'expert' | 'supplier') => {
    setLoading(true);
    setActiveTab(tab);
    // 切换tab时，清空对应不支持的筛选项
    if (searchFilterBarRef.current) {
      searchFilterBarRef.current.setFieldValue(
        tab === 'expert' ? 'show_only_recommend_rollback' : 'plan_suggestion',
        undefined
      );
    }
  });

  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleFullChange = useMemoizedFn((isFullscreen: boolean) => {
    setIsFullscreen(isFullscreen);
    props.setIsFullscreen?.(isFullscreen);
  });

  const scroll = {
    x: 'max-content', y: isFullscreen ? window.innerHeight - 190 * viewBase : 560 
  }

  return (
    <Space direction="vertical" size={12} className="w-full">
      <div ref={wrapperRef} className="w-full">
        {/* Tabs */}
        <div className={cx('kol-table-tabs')}>
          <div className="flex items-center gap-2">
            <Radio.Group
              value={activeTab}
              onChange={(e) => handleTabChange(e.target.value)}
            >
              {TAB_OPTIONS.map((option) => {
                return (
                  <Radio.Button
                    checked={option.value === activeTab}
                    key={option.value}
                    value={option.value}
                  >
                    <div className="inline-flex items-center gap-1">
                      <span>{option.name}</span>
                    </div>
                  </Radio.Button>
                );
              })}
            </Radio.Group>
            {activeTab === 'expert' && <Button 
              type="checked-neutral" 
              icon={<SignOut />}
              onClick={() => {
                // 如果不是最最新一轮，则使用历史轮次数据
                if (currentRound !== turnsOptions[0].value) {
                  window.open(`/api/v2/java_proxy/demands/export_detail_his_round_kol?demand_id=${demandId}&turns_number=${currentRound}`);
                } else {
                  window.open(`/api/v2/java_proxy/demands/export_detail_kol?id=${demandId}`);
                }
              }}>
              导出全部数据
            </Button>}
          </div>
          

          {/* 筛选栏 */}
          <FilterBar
            roundOptions={turnsOptions}
            currentRound={currentRound}
            onRoundChange={onRoundChange}
            viewCurrency={viewCurrency}
            onViewCurrencyChange={setViewCurrency}
            supplierFilter={
              filterData.only_lowest_price
                ? SupplierFilterEnum.LOWEST
                : SupplierFilterEnum.SHOW_ALL
            }
            onSupplierFilterChange={handleSupplierFilterChange}
            showSupplierFilter={activeTab === 'expert'}
            rightSlot={
              <FullScreenButton
                fullRef={wrapperRef}
                handleFullChange={handleFullChange}
              />
            }
          />
        </div>

        {/* 搜索筛选栏 */}
        <SearchFilterBar
          key={activeTab}
          ref={searchFilterBarRef}
          filterConfig={filterConfig}
          disableAddButton={activeTab === 'supplier'}
          // defaultKeys={
          //   activeTab !== 'supplier'
          //     ? [
          //         'principal_confirm',
          //         'plan_suggestion',
          //         'main_platform',
          //         'custom_inspection',
          //         'is_coop_last6m',
          //         'exclusive_sign',
          //         'follower_range',
          //         'initial_quote_range',
          //       ]
          //     : undefined
          // }
          onChange={(params) =>
            setFilterData((prev) => ({ ...prev, ...params }))
          }
        />

        {/* 表格内容 */}
        <div className="kol-table-content">
          <ExpertView
            className={activeTab !== 'expert' && 'hidden'}
            showPlannerAuditColumn={finalShowPlannerAuditColumn}
            showMarketingAuditColumn={finalShowMarketingAuditColumn}
            ref={expertTableRef}
            onDataChange={handleExpertDataChange}
            data={data}
            viewCurrency={finalViewCurrency}
            loading={loading}
            category={category}
            isFullscreen={isFullscreen}
            scroll={scroll}
          />
          <SupplierView
            className={activeTab !== 'supplier' && 'hidden'}
            data={data}
            ref={supplierTableRef}
            onDataChange={handleDataChange}
            viewCurrency={finalViewCurrency}
            loading={loading}
            category={category}
            isFullscreen={isFullscreen}
            scroll={scroll}
          />
        </div>
      </div>
      <RejectSuppliers data={rejectSupplierData} showTag={showSupplierTag} />
    </Space>
  );
});

export default KOLTable;
